import { translate } from '@util/Localization';
import { NervaProductAddons } from 'hydra-data-types';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../config/Color';
import { useAppStore } from '../../store/Store';
import { PricingSelector } from '../../store/pricing/PricingActions';
import { PurchaseSelector, dispatchSetAddonProduct } from '../../store/purchase/PurchaseActions';
import { Checkbox } from '../functional/Checkbox';
import { BodyXS } from '../styles/Typography';

type ProductAddonProps = {
  product: NervaProductAddons;
};

/**
 * Addon product is a non-recurring Stripe product that can be added to checkout
 * This is presented as a checkbox, where every toggle runs an explicit action.
 * The side effect of this action includes updating the purchase intent
 */
export const ProductAddon: FunctionComponent<ProductAddonProps> = ({ product }) => {
  // get the dynamic price of the addon via a selector
  const { addonPlanPrice, addonPlan } = useAppStore((state) => ({
    addonPlanPrice: PricingSelector.priceForAddonPlan(state, product),
    addonPlan: PurchaseSelector.addonPlan(state.purchase),
  }));
  const [addProduct, setAddProduct] = useState<boolean>(addonPlan !== undefined ? true : false);

  function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    const isChecked = e.target.checked;
    setAddProduct(isChecked);
    dispatchSetAddonProduct(isChecked);
  }

  return (
    <AddonContainer data-test="addonContainer">
      <AddonItem>
        <AddonBody>
          <Title>{translate('payment.addonProducts.IBSExpertsQA.title')}</Title>
          <Paragraph>
            {translate('payment.addonProducts.IBSExpertsQA.body', {
              price: addonPlanPrice?.valueOf(),
            })}
          </Paragraph>
        </AddonBody>
        <AddonFooter>
          <Checkbox id="addonPlan" selected={addProduct} onChange={handleOnChange}>
            {translate('payment.addonProducts.IBSExpertsQA.cta')}
          </Checkbox>
        </AddonFooter>
      </AddonItem>
    </AddonContainer>
  );
};

const AddonContainer = styled.div`
  margin: ${({ theme }) => `${theme.spacing._24px} 0`};
  background-color: ${Color.surface.muted};
  border-color: ${Color.subscription.addon};
  border-style: solid;
  border-width: 4px;
  border-radius: 12px;
  text-align: left;
  color: ${Color.content.default};
`;

const Title = styled(BodyXS)`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const Paragraph = styled(BodyXS)`
  margin: 0 0;
`;

const AddonBody = styled.div`
  padding: ${({ theme }) => theme.spacing._16px};
`;

const AddonFooter = styled.div`
  display: block;
  padding: ${({ theme }) => `${theme.spacing._12px} ${theme.spacing._16px}`};
  background-color: ${Color.subscription.addon};
`;

const AddonItem = styled.div`
  align-items: center;
`;
